import { useState } from "react";
import { AvatarFace } from "./AvatarFace";
import { ChevronIcon } from "./Icons";
import { randomSeed } from "./seed";
import { palette } from "./tokens";
import { t } from "../locale";

/**
 * Avatar carousel — the selected face in the middle, the neighbouring two
 * shrunk and dimmed on either side.
 *
 * Faces are generated rather than chosen from a set, so there is no catalogue to
 * page through and no natural end. The list therefore grows as you walk it: a
 * new seed is minted whenever you step past either edge, and the ones you have
 * already seen stay put behind you. Stepping back and forth returns the same
 * faces, which is the whole point of arrows over a shuffle button.
 */

const CURRENT_PX = 96;
const PREVIEW_PX = 56;

interface Carousel {
  seeds: string[];
  index: number;
}

/**
 * Guarantees a neighbour on each side, so the previews never blank out at an
 * edge. Called on every step, which is what makes the list grow.
 */
const padded = ({ seeds, index }: Carousel): Carousel => {
  let next = seeds;
  let at = index;
  if (at === 0) {
    next = [randomSeed(), ...next];
    at = 1;
  }
  if (at === next.length - 1) {
    next = [...next, randomSeed()];
  }
  return { seeds: next, index: at };
};

export const AvatarPicker = ({
  initialSeed,
  onChange,
}: {
  initialSeed: string;
  onChange: (seed: string) => void;
}) => {
  const [carousel, setCarousel] = useState<Carousel>(() =>
    padded({ seeds: [initialSeed], index: 0 }),
  );

  const step = (delta: number) => {
    const next = padded({
      seeds: carousel.seeds,
      index: carousel.index + delta,
    });
    setCarousel(next);
    onChange(next.seeds[next.index]);
  };

  const { seeds, index } = carousel;

  /*
   * The previews are tappable too — on a phone the face you want is a larger
   * target than the arrow beside it. They stay out of the accessibility tree:
   * the arrows already expose both actions, and announcing each face twice
   * would only add noise.
   */
  const preview = (seed: string, delta: number) => (
    <button
      aria-hidden="true"
      tabIndex={-1}
      className="min-h-0 min-w-0 opacity-40 transition-opacity"
      onClick={() => step(delta)}
    >
      <AvatarFace seed={seed} size={PREVIEW_PX} />
    </button>
  );

  return (
    <div className="flex w-full items-center justify-center gap-1">
      <button
        aria-label={t.name.previousAvatar}
        className="flex items-center justify-center"
        onClick={() => step(-1)}
      >
        <ChevronIcon color={palette.inkDim} direction="left" />
      </button>

      <div className="flex items-center gap-3">
        {preview(seeds[index - 1], -1)}
        <AvatarFace seed={seeds[index]} size={CURRENT_PX} label={t.name.avatar} />
        {preview(seeds[index + 1], 1)}
      </div>

      <button
        aria-label={t.name.nextAvatar}
        className="flex items-center justify-center"
        onClick={() => step(1)}
      >
        <ChevronIcon color={palette.inkDim} direction="right" />
      </button>
    </div>
  );
};
